import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Phone, MessageSquare, Mail, Image as ImageIcon, Users, UserPlus, AlertTriangle, Plus, Trash2, Search } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { LEAD_STATUSES, FINANCING_STATUSES } from "@/lib/config";
import { Card, Badge, Btn, EmptyState, inputCls, labelCls, ASelect, AInput, ATextarea } from "../AdminUI";

const SERVICE_OPTIONS = ["House Wash", "Building Exterior Wash", "Commercial Exterior Cleaning", "Pressure Washing", "Soft Washing", "Driveway", "Sidewalk", "Concrete", "Roof Wash", "Deck / Patio", "Fence", "Gutters", "Parking Lot", "Dumpster Pad", "Custom Service"];

const statusColor = (s) => {
  if (s === "Won") return "green"; if (s === "Lost" || s === "Spam") return "red";
  if (s?.includes("Commercial")) return "navy"; if (s?.includes("Callback")) return "amber"; return "blue";
};

function ConvertModal({ lead, onClose, onDone }) {
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/admin/leads/${lead.id}/dedup`).then((r) => setMatches(r.data.matches)).catch(() => setMatches([])); }, [lead.id]);
  const convert = async (merge_into) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/leads/${lead.id}/convert`, merge_into ? { merge_into } : {});
      toast.success(`Converted to ${r.data.customer_id}`); onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Convert failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" data-testid="convert-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-head font-bold text-xl text-[var(--navy)] uppercase">Convert Lead to Customer</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-500">{lead.name} · {lead.phone || lead.email}</p>
        {matches === null ? <p className="text-sm text-gray-400">Checking for duplicates…</p> : matches.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="dedup-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Possible existing customer{matches.length > 1 ? "s" : ""} found (matching email, phone, or address). Merge to avoid duplicates.</span>
            </div>
            {matches.map((m) => (
              <div key={m.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                <div><p className="font-semibold text-sm text-[var(--navy)]">{m.name}</p><p className="text-xs text-gray-400">{m.customer_id} · {m.phone || m.email}</p></div>
                <Btn variant="blue" loading={busy} onClick={() => convert(m.id)} data-testid={`merge-${m.customer_id}`}>Merge</Btn>
              </div>
            ))}
            <div className="pt-2 border-t border-gray-100">
              <Btn variant="ghost" loading={busy} onClick={() => convert(null)} className="w-full" data-testid="create-new-customer">Create as New Customer Instead</Btn>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-green-700 bg-green-50 rounded-lg p-3">No duplicates found. A new customer record will be created and linked to this lead.</p>
            <Btn loading={busy} onClick={() => convert(null)} className="w-full" data-testid="confirm-convert">Create Customer</Btn>
          </div>
        )}
      </div>
    </div>
  );
}

function LeadDetail({ id, onClose, onChange }) {
  const [l, setL] = useState(null);
  const [note, setNote] = useState("");
  const [showConvert, setShowConvert] = useState(false);
  const load = () => api.get(`/admin/leads/${id}`).then((r) => setL(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!l) return null;
  const patch = async (body, msg = "Updated") => {
    try { await api.patch(`/admin/leads/${id}`, body); toast.success(msg); load(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/admin/leads/${id}/notes`, { text: note }); setNote(""); toast.success("Note added"); load(); }
    catch { toast.error("Failed"); }
  };
  const financing = async (status) => {
    try { await api.post(`/admin/leads/${id}/financing`, { status }); toast.success("Financing updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const Field = ({ label, value }) => value ? <div><p className="text-[11px] uppercase text-gray-400 font-semibold">{label}</p><p className="text-sm text-[var(--navy)]">{value}</p></div> : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-detail">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[var(--surface)] h-full overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between z-10">
          <div><h2 className="font-head font-bold text-xl text-[var(--navy)]">{l.name || "Unknown"}</h2>
            <p className="text-xs text-gray-400">{l.lead_id} · {new Date(l.created_at).toLocaleString()}</p></div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 text-sm bg-[var(--red)] text-white px-3 py-1.5 rounded-md"><Phone className="w-4 h-4" /> Call</a>}
            {l.phone && <a href={`sms:${l.phone}`} className="flex items-center gap-1.5 text-sm bg-[var(--action)] text-white px-3 py-1.5 rounded-md"><MessageSquare className="w-4 h-4" /> Text</a>}
            {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 text-sm bg-[var(--navy)] text-white px-3 py-1.5 rounded-md"><Mail className="w-4 h-4" /> Email</a>}
            {l.status !== "Existing Customer" && <button onClick={() => setShowConvert(true)} className="flex items-center gap-1.5 text-sm bg-green-600 text-white px-3 py-1.5 rounded-md" data-testid="convert-lead-btn"><UserPlus className="w-4 h-4" /> Convert to Customer</button>}
            {l.customer_ref && <Badge color="green">Converted</Badge>}
            <Badge color="green">Email notifications active (SMTP2GO)</Badge>
          </div>
          {showConvert && <ConvertModal lead={l} onClose={() => setShowConvert(false)} onDone={() => { load(); onChange?.(); }} />}

          <Card title="Details">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone" value={l.phone} /><Field label="Email" value={l.email} />
              <Field label="Address" value={l.address} /><Field label="City" value={`${l.city || ""} ${l.state || ""} ${l.zip || ""}`} />
              <Field label="Property Type" value={l.property_type} /><Field label="Services" value={l.services?.join(", ")} />
              <Field label="Surface" value={l.surface_type} /><Field label="Company" value={l.company_name} />
              <Field label="Preferred Contact" value={l.preferred_contact} /><Field label="Callback" value={`${l.preferred_date || ""} ${l.preferred_time || ""}`} />
              <Field label="Heard About" value={l.heard_about} /><Field label="Source" value={l.source} />
              <Field label="Landing Page" value={l.landing_page} /><Field label="Priority" value={l.priority} />
            </div>
            {l.description && <div className="mt-3"><p className="text-[11px] uppercase text-gray-400 font-semibold">Description</p><p className="text-sm">{l.description}</p></div>}
            {l.additional_notes && <div className="mt-3"><p className="text-[11px] uppercase text-gray-400 font-semibold">Notes from customer</p><p className="text-sm">{l.additional_notes}</p></div>}
            {l.ai_summary && <div className="mt-3"><p className="text-[11px] uppercase text-gray-400 font-semibold">AI Summary</p><p className="text-sm">{l.ai_summary}</p></div>}
            <p className="text-xs text-gray-400 mt-3">Consent: {l.consent ? "Yes" : "Not recorded"}</p>
          </Card>

          {l.photo_ids?.length > 0 && (
            <Card title="Uploaded Photos (Private)">
              <div className="grid grid-cols-3 gap-2">
                {l.photo_ids.map((pid) => <img key={pid} src={fileUrl(pid)} alt="Customer upload" className="rounded-lg h-28 w-full object-cover" />)}
              </div>
            </Card>
          )}

          <Card title="Manage">
            <div className="grid grid-cols-2 gap-3">
              <ASelect label="Status" value={l.status} options={LEAD_STATUSES} onChange={(e) => patch({ status: e.target.value })} data-testid="lead-status-select" />
              <ASelect label="Priority" value={l.priority} options={["High", "Medium", "Low"]} onChange={(e) => patch({ priority: e.target.value })} />
              <AInput label="Assigned To" defaultValue={l.assigned_to} onBlur={(e) => patch({ assigned_to: e.target.value })} />
              <AInput label="Follow-up Date" type="date" defaultValue={l.follow_up_date} onBlur={(e) => patch({ follow_up_date: e.target.value })} />
              <AInput label="Estimated Value ($)" type="number" defaultValue={l.estimated_value || ""} onBlur={(e) => patch({ estimated_value: parseFloat(e.target.value) || 0 })} />
              <AInput label="Completed Value ($)" type="number" defaultValue={l.completed_value || ""} onBlur={(e) => patch({ completed_value: parseFloat(e.target.value) || 0 })} />
            </div>
            <div className="flex gap-2 mt-3">
              <Btn variant="green" onClick={() => patch({ status: "Won" }, "Marked Won")}>Mark Won</Btn>
              <Btn variant="danger" onClick={() => patch({ status: "Lost" }, "Marked Lost")}>Mark Lost</Btn>
            </div>
          </Card>

          <Card title="Client Financing">
            <div className="flex items-center gap-2 mb-3"><Badge color="blue">{l.financing_status}</Badge>
              <Badge color="amber">Live submission disabled</Badge></div>
            <ASelect label="Financing Status" value={l.financing_status} options={FINANCING_STATUSES} onChange={(e) => financing(e.target.value)} data-testid="financing-status" />
            <p className="text-xs text-gray-400 mt-2">Financing is subject to application, approval, and EasyQuickFund's terms.</p>
          </Card>

          <Card title="Email Notifications">
            <div className="flex items-center gap-2 mb-3">
              <Btn variant="blue" onClick={async () => {
                try { await api.post(`/admin/leads/${id}/resend-notification`); toast.success("Notification retry queued"); setTimeout(load, 1500); }
                catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }} data-testid="resend-notification">Retry Notification</Btn>
            </div>
            {(l.email_activity || []).length === 0 ? (
              <p className="text-sm text-gray-400">No email activity yet.</p>
            ) : (
              <div className="space-y-2">
                {(l.email_activity || []).slice().reverse().map((e, i) => (
                  <div key={i} className="text-sm flex items-start gap-2">
                    <Badge color={e.status === "sent" ? "green" : e.status === "failed" ? "red" : "amber"}>{e.status}</Badge>
                    <div><span>{e.text}</span><p className="text-[11px] text-gray-400">{new Date(e.at).toLocaleString()}</p></div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title="Notes">
            <div className="flex gap-2">
              <input className={inputCls} placeholder="Add an internal note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" onKeyDown={(e) => e.key === "Enter" && addNote()} />
              <Btn onClick={addNote} data-testid="add-note-btn">Add</Btn>
            </div>
            <div className="mt-3 space-y-2">
              {(l.notes || []).map((n, i) => <div key={i} className="text-sm bg-gray-50 rounded-md p-2.5"><p>{n.text}</p><p className="text-[11px] text-gray-400">{n.by} · {new Date(n.at).toLocaleString()}</p></div>)}
            </div>
          </Card>

          <Card title="Activity Timeline">
            <div className="space-y-2">
              {(l.activity || []).slice().reverse().map((a, i) => (
                <div key={i} className="flex gap-3 text-sm"><span className="text-gray-400 text-xs w-32 shrink-0">{new Date(a.at).toLocaleString()}</span><span>{a.text}</span></div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function LeadConfirm({ title, body, confirmLabel, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4" data-testid="lead-confirm">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onCancel} />
      <div className="relative bg-white rounded-2xl w-full max-w-md p-6 space-y-4">
        <h2 className="font-head font-bold text-lg text-[var(--navy)] uppercase">{title}</h2>
        <p className="text-sm text-gray-600 whitespace-pre-line">{body}</p>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onCancel} disabled={busy} data-testid="lead-confirm-cancel">Cancel</Btn>
          <Btn variant="danger" onClick={onConfirm} loading={busy} data-testid="lead-confirm-delete">{confirmLabel}</Btn>
        </div>
      </div>
    </div>
  );
}

function NewLeadModal({ onClose, onCreated }) {
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", address: "", city: "", state: "FL", zip: "", property_type: "residential", services: [], classification: "", status: "New Lead", company_name: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleSvc = (s) => setF((p) => ({ ...p, services: p.services.includes(s) ? p.services.filter((x) => x !== s) : [...p.services, s] }));
  const create = async (force) => {
    if (!f.first_name && !f.last_name) { toast.error("Enter a first or last name."); return; }
    if (!f.phone && !f.email) { toast.error("Enter a phone number or email."); return; }
    setBusy(true);
    try {
      if (!force) {
        const r = await api.post("/admin/leads/check-duplicate", { phone: f.phone, email: f.email });
        if (r.data.matches?.length) { setDup(r.data.matches); setBusy(false); return; }
      }
      await api.post("/admin/leads", { ...f });
      toast.success("Lead created successfully."); onCreated(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create lead."); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" data-testid="new-lead-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-head font-bold text-xl text-[var(--navy)] uppercase">New Lead</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        {dup && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 space-y-2" data-testid="lead-dup-warning">
            <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-4 h-4" /> Possible Existing Record</p>
            <p>We found an existing Lead or Customer with matching contact info:</p>
            {dup.map((m, i) => <p key={i} className="text-xs">• {m.name} ({m.kind} {m.lead_id}) — {m.phone || m.email}</p>)}
            <div className="flex gap-2 pt-1"><Btn variant="danger" loading={busy} onClick={() => create(true)} data-testid="lead-create-anyway">Create New Lead Anyway</Btn><Btn variant="ghost" onClick={() => setDup(null)}>Back</Btn></div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <AInput label="First Name" value={f.first_name} onChange={set("first_name")} data-testid="nl-first" />
          <AInput label="Last Name" value={f.last_name} onChange={set("last_name")} data-testid="nl-last" />
          <AInput label="Phone" value={f.phone} onChange={set("phone")} data-testid="nl-phone" />
          <AInput label="Email" type="email" value={f.email} onChange={set("email")} data-testid="nl-email" />
          <AInput label="Property / Service Address" value={f.address} onChange={set("address")} />
          <AInput label="Company (optional)" value={f.company_name} onChange={set("company_name")} />
          <AInput label="City" value={f.city} onChange={set("city")} />
          <AInput label="State" value={f.state} onChange={set("state")} />
          <AInput label="ZIP" value={f.zip} onChange={set("zip")} />
          <ASelect label="Lead Type" value={f.property_type} options={["residential", "commercial", "other"]} onChange={set("property_type")} data-testid="nl-type" />
          <ASelect label="Status" value={f.status} options={LEAD_STATUSES} onChange={set("status")} data-testid="nl-status" />
          <ASelect label="Classification (optional)" value={f.classification} options={["", "Qualified Residential Lead", "Qualified Commercial Lead", "Callback Requested", "Needs Human Review"]} onChange={set("classification")} />
        </div>
        <div>
          <label className={labelCls}>Services Interested In</label>
          <div className="flex flex-wrap gap-1.5">
            {SERVICE_OPTIONS.map((s) => (
              <button key={s} type="button" onClick={() => toggleSvc(s)} className={`text-xs px-2.5 py-1.5 rounded-full border ${f.services.includes(s) ? "bg-[var(--navy)] text-white border-[var(--navy)]" : "bg-white text-gray-600 border-gray-200"}`}>{s}</button>
            ))}
          </div>
        </div>
        <ATextarea label="Notes / Description" value={f.description} onChange={set("description")} rows={2} />
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={() => create(false)} loading={busy} data-testid="nl-create"><Plus className="w-4 h-4" /> Create Lead</Btn>
        </div>
      </div>
    </div>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null); // {mode:'single'|'bulk', lead?}
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/leads${filter ? `?status=${encodeURIComponent(filter)}` : ""}`).then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const visible = leads.filter((l) => !q || [l.name, l.phone, l.email, l.lead_id].some((v) => (v || "").toLowerCase().includes(q.toLowerCase())));
  const allSel = visible.length > 0 && visible.every((l) => sel.has(l.id));
  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel(allSel ? new Set() : new Set(visible.map((l) => l.id)));
  const clearSel = () => setSel(new Set());

  const delOne = async (l) => {
    setBusy(true);
    try {
      await api.delete(`/admin/leads/${l.id}`);
      toast.success("Lead deleted.");
      setLeads((p) => p.filter((x) => x.id !== l.id));
      setSel((p) => { const n = new Set(p); n.delete(l.id); return n; });
      if (openId === l.id) setOpenId(null);
      setConfirm(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Unable to delete lead."); }
    finally { setBusy(false); }
  };
  const delBulk = async () => {
    const ids = [...sel]; setBusy(true);
    try {
      const r = await api.post("/admin/leads/bulk-delete", { lead_ids: ids });
      const { deleted = 0, failed = 0 } = r.data || {};
      failed > 0 ? toast.warning(`${deleted} lead${deleted === 1 ? "" : "s"} deleted. ${failed} could not be deleted.`) : toast.success(`${deleted} lead${deleted === 1 ? "" : "s"} deleted.`);
      const s = new Set(ids);
      setLeads((p) => p.filter((x) => !s.has(x.id)));
      if (openId && s.has(openId)) setOpenId(null);
      clearSel(); setConfirm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Leads</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className={`${inputCls} w-auto pl-8`} placeholder="Search leads…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="lead-search" />
          </div>
          <select className={`${inputCls} w-auto`} value={filter} onChange={(e) => { setFilter(e.target.value); clearSel(); }} data-testid="lead-filter">
            <option value="">All statuses</option>{LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <Btn onClick={() => setShowNew(true)} data-testid="new-lead-btn"><Plus className="w-4 h-4" /> New Lead</Btn>
        </div>
      </div>

      {sel.size > 0 && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="lead-bulk-bar">
          <span className="text-sm font-semibold text-red-700" data-testid="lead-selected-count">{sel.size} selected</span>
          <div className="ml-auto flex gap-2">
            <Btn variant="ghost" className="!py-1 !px-2 !text-xs" onClick={clearSel} data-testid="lead-clear-sel">Clear Selection</Btn>
            <Btn variant="danger" className="!py-1 !px-2 !text-xs" onClick={() => setConfirm({ mode: "bulk" })} data-testid="lead-delete-selected"><Trash2 className="w-3.5 h-3.5" /> Delete Selected</Btn>
          </div>
        </div>
      )}

      <Card>
        {visible.length === 0 ? <EmptyState icon={Users} title="No leads yet" hint="Quote requests, Ghost AI, and manual leads appear here." /> : (
          <div className="overflow-x-auto -m-1">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-400 text-xs uppercase border-b border-gray-100">
                <th className="py-2 px-2"><input type="checkbox" checked={allSel} onChange={toggleAll} className="w-4 h-4 accent-[var(--action)]" data-testid="lead-select-all" /></th>
                <th className="px-2">Lead</th><th className="px-2">Contact</th><th className="px-2">Type</th><th className="px-2">Classification</th><th className="px-2">Status</th><th className="px-2">Created</th><th className="px-2"></th>
              </tr></thead>
              <tbody>
                {visible.map((l) => (
                  <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`lead-row-${l.lead_id}`}>
                    <td className="px-2" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={sel.has(l.id)} onChange={() => toggle(l.id)} className="w-4 h-4 accent-[var(--action)]" data-testid={`lead-check-${l.lead_id}`} aria-label="Select lead" /></td>
                    <td className="py-3 px-2 cursor-pointer" onClick={() => setOpenId(l.id)}><p className="font-semibold text-[var(--navy)]">{l.name || "Unknown"}</p><p className="text-xs text-gray-400">{l.lead_id}</p></td>
                    <td className="px-2 text-gray-600 cursor-pointer" onClick={() => setOpenId(l.id)}>{l.phone || l.email || "—"}</td>
                    <td className="px-2 capitalize cursor-pointer" onClick={() => setOpenId(l.id)}>{l.property_type}</td>
                    <td className="px-2 cursor-pointer" onClick={() => setOpenId(l.id)}><span className="text-xs text-gray-600">{l.classification}</span></td>
                    <td className="px-2 cursor-pointer" onClick={() => setOpenId(l.id)}><Badge color={statusColor(l.status)}>{l.status}</Badge></td>
                    <td className="px-2 text-xs text-gray-400 cursor-pointer" onClick={() => setOpenId(l.id)}>{new Date(l.created_at).toLocaleDateString()}</td>
                    <td className="px-2"><button onClick={() => setConfirm({ mode: "single", lead: l })} title="Delete lead" aria-label="Delete lead" className="p-1.5 text-gray-300 hover:text-red-500" data-testid={`lead-delete-${l.lead_id}`}><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openId && <LeadDetail id={openId} onClose={() => setOpenId(null)} onChange={load} />}
      {showNew && <NewLeadModal onClose={() => setShowNew(false)} onCreated={load} />}
      {confirm?.mode === "single" && <LeadConfirm title="Delete Lead?" body={`${confirm.lead.name || "Unknown"}\n${confirm.lead.lead_id}\n\nThis will permanently delete this Lead record. Linked Customers, Ghost AI conversations, and other business records will remain.`} confirmLabel="Delete Lead" busy={busy} onCancel={() => setConfirm(null)} onConfirm={() => delOne(confirm.lead)} />}
      {confirm?.mode === "bulk" && <LeadConfirm title={`Delete ${sel.size} Lead${sel.size === 1 ? "" : "s"}?`} body={"This will permanently delete the selected Lead records.\n\nLinked Customers, Jobs, Quotes, Invoices, Payments and other business records will NOT be deleted."} confirmLabel={`Delete ${sel.size} Lead${sel.size === 1 ? "" : "s"}`} busy={busy} onCancel={() => setConfirm(null)} onConfirm={delBulk} />}
    </div>
  );
}
